import type { Router } from 'vue-router'
import type { TourStep } from '../types/tour.types'
import { waitForElement } from './tour-navigation'

/**
 * Wires the auto-advance condition for a step. Returns a cleanup function that
 * removes every listener/observer it created. `next-click` (the default) needs
 * no wiring — the popover button drives it.
 */
export function createStepAdvancer(
  step: TourStep,
  targetEl: Element | null,
  ctx: { onAdvance: () => void; router: Router | null },
): () => void {
  const adv = step.advanceOn
  if (!adv || adv.type === 'next-click') return () => {}

  const cleanups: (() => void)[] = []

  switch (adv.type) {
    case 'target-click': {
      if (targetEl) {
        const handler = () => ctx.onAdvance()
        targetEl.addEventListener('click', handler, { once: true })
        cleanups.push(() => targetEl.removeEventListener('click', handler))
      }
      break
    }
    case 'event': {
      if (adv.value) {
        const name = adv.value
        const handler = () => ctx.onAdvance()
        window.addEventListener(name, handler)
        cleanups.push(() => window.removeEventListener(name, handler))
      }
      break
    }
    case 'dom-appear': {
      if (adv.value) {
        const controller = new AbortController()
        void waitForElement(adv.value, { signal: controller.signal }).then((el) => {
          if (el) ctx.onAdvance()
        })
        cleanups.push(() => controller.abort())
      }
      break
    }
    case 'route': {
      if (adv.value && ctx.router) {
        const off = ctx.router.afterEach((to) => {
          if (to.name === adv.value) {
            off()
            ctx.onAdvance()
          }
        })
        cleanups.push(off)
      }
      break
    }
  }

  return () => cleanups.forEach((fn) => fn())
}
